/**
 * API Response Validator
 *
 * Validates API responses using Zod schemas to ensure type safety at runtime.
 * This prevents malformed data from corrupting the frontend state.
 */

import { z } from "zod";

// ============================================================================
// VALIDATION RESULT TYPES
// ============================================================================

export interface ValidationSuccess<T> {
  success: true;
  data: T;
}

export interface ValidationError {
  success: false;
  error: string;
  details?: z.ZodError;
}

export type ValidationResult<T> = ValidationSuccess<T> | ValidationError;

// ============================================================================
// VALIDATION OPTIONS
// ============================================================================

export interface ValidationOptions {
  /**
   * Log validation errors to console
   * @default true in development, false in production
   */
  logErrors?: boolean;

  /**
   * Throw error on validation failure instead of returning error object
   * @default false
   */
  throwOnError?: boolean;

  /**
   * Context information for better error messages
   */
  context?: string;
}

// ============================================================================
// CORE VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate a single item against a Zod schema
 */
export function validateItem<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  options: ValidationOptions = {},
): ValidationResult<T> {
  const { logErrors = process.env.NODE_ENV === "development", throwOnError = false, context } = options;

  try {
    const result = schema.safeParse(data);

    if (result.success) {
      return {
        success: true,
        data: result.data,
      };
    }

    const errorMessage = context
      ? `Validation failed for ${context}: ${result.error.message}`
      : `Validation failed: ${result.error.message}`;

    if (logErrors) {
      console.error("[API Validator]", errorMessage);
      console.error("[API Validator] Details:", result.error.format());
      console.error("[API Validator] Received data:", data);
    }

    if (throwOnError) {
      throw new Error(errorMessage);
    }

    return {
      success: false,
      error: errorMessage,
      details: result.error,
    };
  } catch (error) {
    const errorMessage = context
      ? `Unexpected error validating ${context}: ${error instanceof Error ? error.message : "Unknown error"}`
      : `Unexpected error during validation: ${error instanceof Error ? error.message : "Unknown error"}`;

    if (logErrors) {
      console.error("[API Validator]", errorMessage, error);
    }

    if (throwOnError) {
      throw error;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Validate an array of items against a Zod schema
 */
export function validateArray<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  options: ValidationOptions = {},
): ValidationResult<T[]> {
  const { logErrors = process.env.NODE_ENV === "development", throwOnError = false, context } = options;

  if (!Array.isArray(data)) {
    const errorMessage = context ? `Expected array for ${context}, got ${typeof data}` : "Expected array";

    if (logErrors) {
      console.error("[API Validator]", errorMessage);
    }

    if (throwOnError) {
      throw new Error(errorMessage);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }

  const arraySchema = z.array(schema);
  return validateItem(arraySchema, data, { ...options, context: context || "array" });
}

/**
 * Validate API response with standard { data, error } structure
 */
export function validateApiResponse<T>(
  schema: z.ZodSchema<T>,
  response: unknown,
  options: ValidationOptions = {},
): ValidationResult<T> {
  const { context } = options;

  // Check if response has the expected structure
  if (!response || typeof response !== "object") {
    return {
      success: false,
      error: context ? `Invalid API response structure for ${context}` : "Invalid API response structure",
    };
  }

  const responseObj = response as Record<string, unknown>;

  // Check for error in response
  if (responseObj.error) {
    return {
      success: false,
      error: typeof responseObj.error === "string" ? responseObj.error : "API returned an error",
    };
  }

  // Validate the data field
  if (!("data" in responseObj)) {
    return {
      success: false,
      error: context ? `Missing 'data' field in API response for ${context}` : "Missing 'data' field in API response",
    };
  }

  return validateItem(schema, responseObj.data, options);
}

/**
 * Validate API response with array data
 */
export function validateApiArrayResponse<T>(
  schema: z.ZodSchema<T>,
  response: unknown,
  options: ValidationOptions = {},
): ValidationResult<T[]> {
  const { context } = options;

  // Check if response has the expected structure
  if (!response || typeof response !== "object") {
    return {
      success: false,
      error: context ? `Invalid API response structure for ${context}` : "Invalid API response structure",
    };
  }

  const responseObj = response as Record<string, unknown>;

  // Check for error in response
  if (responseObj.error) {
    return {
      success: false,
      error: typeof responseObj.error === "string" ? responseObj.error : "API returned an error",
    };
  }

  // Validate the data field
  if (!("data" in responseObj)) {
    return {
      success: false,
      error: context ? `Missing 'data' field in API response for ${context}` : "Missing 'data' field in API response",
    };
  }

  return validateArray(schema, responseObj.data, options);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract data from validation result or throw error
 */
export function unwrapValidation<T>(result: ValidationResult<T>): T {
  if (result.success) {
    return result.data;
  }
  throw new Error(result.error);
}

/**
 * Extract data from validation result or return default value
 */
export function unwrapValidationOr<T>(result: ValidationResult<T>, defaultValue: T): T {
  if (result.success) {
    return result.data;
  }
  return defaultValue;
}

/**
 * Extract data from validation result or return null
 */
export function unwrapValidationOrNull<T>(result: ValidationResult<T>): T | null {
  if (result.success) {
    return result.data;
  }
  return null;
}
